/**
 * BusinessProfilePage — the /business/:slug page. Renders the modern
 * business "website" the spec describes: hero, tabs, and per-section
 * skeleton content. Each tab is a stable route target so team-members
 * can start filling them in over multiple sessions without breaking
 * links.
 *
 * The **Shop** tab is gated on the business's plan tier — only
 * ``ecommerce_*`` tiers show it. Free-tier owners see the Shop tab
 * with an upsell.
 *
 * Data source: ``GET /api/businesses/by-slug/{slug}`` — public.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, MapPin, Phone, Mail, Globe, Clock,
  Package, Wrench, Users, Image as ImageIcon, Star,
  Newspaper, Calendar, ShoppingBag, MessageCircleQuestion, Sparkles, Briefcase,
  Loader2, ExternalLink, ShieldCheck,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview",  Icon: Building2 },
  { id: "products", label: "Products",  Icon: Package,   ecommerceHint: true },
  { id: "services", label: "Services",  Icon: Wrench },
  { id: "team",     label: "Team",      Icon: Users },
  { id: "gallery",  label: "Gallery",   Icon: ImageIcon },
  { id: "events",   label: "Events",    Icon: Calendar },
  { id: "jobs",     label: "Jobs",      Icon: Briefcase },
  { id: "news",     label: "News",      Icon: Newspaper },
  { id: "reviews",  label: "Reviews",   Icon: Star },
  { id: "faqs",     label: "Q & A",     Icon: MessageCircleQuestion },
  { id: "shop",     label: "Shop",      Icon: ShoppingBag, ecommerce: true },
  { id: "contact",  label: "Contact",   Icon: Phone },
];

function ComingSoon({ title, body }) {
  return (
    <div className="wofy-card !p-8 text-center">
      <Sparkles className="w-8 h-8 mx-auto opacity-40 mb-2" />
      <div className="font-display text-2xl mb-1">{title}</div>
      <p className="text-sm opacity-70 max-w-md mx-auto">{body}</p>
    </div>
  );
}

export default function BusinessProfilePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await http.get(`/businesses/by-slug/${slug}`);
      setBiz(data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(e?.response?.data?.detail || "Could not load this business.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const isOwner = useMemo(
    () => Boolean(biz && user && (biz.owner_id === user.id)),
    [biz, user],
  );
  const isEcommerce = biz?._plan?.ecommerce;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" />
      </div>
    );
  }
  if (notFound || !biz) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Building2 className="w-10 h-10 mx-auto opacity-40 mb-2" />
        <h1 className="font-display text-3xl mb-2">Business not found</h1>
        <p className="opacity-70">The slug <code>{slug}</code> doesn't match any listed business.</p>
        <Link to="/businesses" className="wofy-btn-ghost inline-flex mt-4">← Back to the Business Portal</Link>
      </div>
    );
  }

  return (
    <div className="pb-16" data-testid={`business-profile-${biz.slug}`}>
      {/* Hero */}
      <div
        className="border-b"
        style={{
          borderColor: "var(--wofy-border)",
          background: biz.hero_image_url
            ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(${biz.hero_image_url}) center/cover`
            : "linear-gradient(135deg, rgba(178,93,56,0.1), rgba(178,93,56,0.02))",
          color: biz.hero_image_url ? "white" : "inherit",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-10 flex flex-col md:flex-row items-start gap-6">
          <div
            className="w-24 h-24 rounded-2xl shrink-0 flex items-center justify-center text-white font-display text-4xl shadow-lg"
            style={{ background: biz.logo_url ? undefined : "var(--wofy-primary)" }}
          >
            {biz.logo_url
              ? <img src={biz.logo_url} alt="" className="w-24 h-24 rounded-2xl object-cover" />
              : biz.name[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-3xl md:text-4xl">{biz.name}</h1>
              {biz._plan?.ecommerce && (
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white border" style={{ color: "var(--wofy-primary)", borderColor: "var(--wofy-primary)" }}>
                  <ShoppingBag className="w-3 h-3 inline mr-0.5" /> Shop
                </span>
              )}
              {biz.industry && (
                <span className="text-[10px] font-mono uppercase tracking-wider opacity-80">{biz.industry}</span>
              )}
            </div>
            {biz.description && <p className="mt-2 max-w-2xl opacity-90">{biz.description}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs opacity-90">
              {(biz.city || biz.country) && (
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[biz.city, biz.country].filter(Boolean).join(", ")}</span>
              )}
              {biz.contact_phone && (
                <a href={`tel:${biz.contact_phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" /> {biz.contact_phone}</a>
              )}
              {biz.contact_email && (
                <a href={`mailto:${biz.contact_email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-3 h-3" /> {biz.contact_email}</a>
              )}
              {biz.website && (
                <a href={biz.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline"><Globe className="w-3 h-3" /> Website</a>
              )}
              {biz.founded_year && (
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Since {biz.founded_year}</span>
              )}
              {biz.verified_at && (
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</span>
              )}
            </div>
            {isOwner && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs">
                <Link to={`/business/${biz.slug}/edit`} className="wofy-btn-ghost !bg-white/90 !text-black inline-flex items-center gap-1.5" data-testid="business-edit-btn">
                  Edit business →
                </Link>
                <span className="opacity-80" data-testid="business-plan-badge">
                  Plan: <b>{biz._plan?.name || biz.plan}</b>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b sticky top-0 z-10 bg-white" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="max-w-6xl mx-auto px-4 flex overflow-x-auto">
          {TABS.map(({ id, label, Icon, ecommerce }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className="inline-flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 whitespace-nowrap"
              style={{
                borderColor: tab === id ? "var(--wofy-primary)" : "transparent",
                color:       tab === id ? "var(--wofy-primary)" : "var(--wofy-text-soft)",
                fontWeight:  tab === id ? 600 : 400,
              }}
              data-testid={`business-tab-${id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {ecommerce && !isEcommerce && <span className="text-[9px] opacity-60">🔒</span>}
            </button>
          ))}
          {(biz.custom_pages || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setTab(`page:${p.id}`)}
              className="inline-flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 whitespace-nowrap"
              style={{
                borderColor: tab === `page:${p.id}` ? "var(--wofy-primary)" : "transparent",
                color:       tab === `page:${p.id}` ? "var(--wofy-primary)" : "var(--wofy-text-soft)",
                fontWeight:  tab === `page:${p.id}` ? 600 : 400,
              }}
              data-testid={`business-tab-page-${p.slug}`}
            >
              {p.title}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {tab === "overview" && <OverviewTab biz={biz} />}
        {tab === "products" && <ProductsTab biz={biz} isOwner={isOwner} />}
        {tab === "services" && <ComingSoon title="Services" body="List consultations, repairs, or professional services with per-service pricing, FAQs, and online booking." />}
        {tab === "team"     && <ComingSoon title="Team" body="Show your leadership, employees, departments and awards. Optionally link team members to their Social Pulse profiles." />}
        {tab === "gallery"  && <ComingSoon title="Photo gallery" body="Unlimited albums — office, products, events, factory tours, team, awards." />}
        {tab === "events"   && <ComingSoon title="Business events" body="Host product launches, exhibitions, training, webinars — integrated with the platform-wide Events calendar." />}
        {tab === "jobs"     && <JobsTab biz={biz} isOwner={isOwner} />}
        {tab === "news"     && <ComingSoon title="News & announcements" body="Press releases, awards, product launches, industry recognition." />}
        {tab === "reviews"  && <ComingSoon title="Customer reviews" body="Verified purchase reviews, star ratings, photos, and business replies." />}
        {tab === "faqs"     && <ComingSoon title="Community Q & A" body="Customers ask questions, the business answers publicly. Great for SEO." />}
        {tab === "shop"     && <ShopTab biz={biz} isOwner={isOwner} isEcommerce={isEcommerce} />}
        {tab === "contact"  && <ContactTab biz={biz} />}
        {tab.startsWith("page:") && (() => {
          const p = (biz.custom_pages || []).find((x) => `page:${x.id}` === tab);
          if (!p) return null;
          return (
            <article className="wofy-card !p-6 prose max-w-none" data-testid={`custom-page-body-${p.slug}`}>
              <h2 className="font-display text-3xl mb-3">{p.title}</h2>
              <div dangerouslySetInnerHTML={{ __html: p.body_html }} />
            </article>
          );
        })()}
      </div>
    </div>
  );
}

function OverviewTab({ biz }) {
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 space-y-4">
        <div className="wofy-card !p-6">
          <div className="overline mb-2">About us</div>
          {biz.description
            ? <p className="whitespace-pre-line leading-relaxed">{biz.description}</p>
            : <p className="opacity-70 italic">This business hasn't written an About Us yet.</p>}
        </div>
        <ComingSoon title="Company timeline" body="Founder story, milestones, awards, expansions — auto-drafted with AI once you fill in the founder's name and year." />
      </div>
      <aside className="space-y-3">
        <div className="wofy-card !p-4">
          <div className="overline mb-2">Contact</div>
          <div className="text-sm space-y-1.5">
            {biz.contact_person && <div><b>{biz.contact_person}</b><div className="text-xs opacity-70">Contact person</div></div>}
            {biz.address && <div className="text-xs opacity-80"><MapPin className="w-3 h-3 inline mr-1" />{biz.address}</div>}
            {biz.contact_phone && <div className="text-xs"><Phone className="w-3 h-3 inline mr-1 opacity-60" />{biz.contact_phone}</div>}
            {biz.contact_email && <div className="text-xs"><Mail className="w-3 h-3 inline mr-1 opacity-60" />{biz.contact_email}</div>}
            {biz.website && <div className="text-xs"><Globe className="w-3 h-3 inline mr-1 opacity-60" /><a href={biz.website} target="_blank" rel="noreferrer" className="underline">{biz.website}</a></div>}
          </div>
        </div>
        {biz.hours && Object.keys(biz.hours).length > 0 && (
          <div className="wofy-card !p-4">
            <div className="overline mb-2">Business hours</div>
            <ul className="text-xs space-y-1">
              {Object.entries(biz.hours).map(([d, h]) => (
                <li key={d}><span className="opacity-60 mr-1">{d}</span>{h}</li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function ProductsTab({ biz, isOwner }) {
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/businesses/${biz.id}/products`);
        setProducts(data.items || []);
      } catch { setProducts([]); }
    })();
  }, [biz.id]);

  const addToCart = async (product) => {
    if (!user) { toast.error("Please sign in to add to cart."); return; }
    setBusy((b) => ({ ...b, [product.id]: true }));
    try {
      await http.post("/cart/items", { product_id: product.id, quantity: 1 });
      toast.success(`Added ${product.name} to cart.`);
      window.dispatchEvent(new CustomEvent("sp:cart-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add."); }
    finally { setBusy((b) => ({ ...b, [product.id]: false })); }
  };

  if (products === null) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>;

  return (
    <div className="space-y-4" data-testid={`products-tab-${biz.slug}`}>
      {isOwner && (
        <div className="flex justify-end">
          <Link to={`/business/${biz.slug}/products/new`} className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="business-products-add">
            <Package className="w-4 h-4" /> Add a product
          </Link>
        </div>
      )}
      {products.length === 0 ? (
        <ComingSoon
          title="No products yet"
          body={isOwner
            ? "Click 'Add a product' to list your first item. Photos + prices + a short description are all it takes."
            : "This business hasn't listed any products yet."}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <div key={p.id} className="wofy-card !p-4 flex flex-col" data-testid={`product-card-${p.id}`}>
              <div className="aspect-square bg-black/5 rounded-lg overflow-hidden mb-3">
                {p.image_url
                  ? <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center opacity-30 text-4xl">📦</div>}
              </div>
              <div className="font-display text-lg leading-tight">{p.name}</div>
              {p.description && <p className="text-xs opacity-80 mt-1 line-clamp-2">{p.description}</p>}
              <div className="mt-3 flex items-center justify-between">
                <span className="font-medium tabular-nums" data-testid={`product-price-${p.id}`}>
                  {new Intl.NumberFormat(undefined, { style: "currency", currency: (p.currency || "usd").toUpperCase() }).format(p.price)}
                </span>
                {isOwner ? (
                  <Link to={`/business/${biz.slug}/products/${p.id}/edit`} className="text-xs underline opacity-80" data-testid={`product-edit-${p.id}`}>Edit</Link>
                ) : (
                  <button onClick={() => addToCart(p)} disabled={busy[p.id]} className="wofy-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-60" data-testid={`product-add-${p.id}`}>
                    {busy[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingBag className="w-3 h-3" />}
                    Add to cart
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JobsTab({ biz, isOwner }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/businesses/${biz.id}/jobs`);
        setItems(data.items || []);
      } catch { setItems([]); }
    })();
  }, [biz.id]);

  if (items === null) return <div className="text-center py-8"><Loader2 className="w-6 h-6 mx-auto animate-spin opacity-60" /></div>;

  return (
    <div className="space-y-4" data-testid={`jobs-tab-${biz.slug}`}>
      {isOwner && (
        <div className="flex justify-end">
          <Link to="/jobs/post" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="business-jobs-post">
            <Briefcase className="w-4 h-4" /> Post a job for {biz.name}
          </Link>
        </div>
      )}
      {items.length === 0 ? (
        <ComingSoon
          title="Careers at this business"
          body={isOwner
            ? "You have no active jobs. Post a role and it'll show up here — plus on the site-wide Jobs board."
            : `${biz.name} isn't hiring right now. Follow the business to be notified when they post a job.`}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((j) => (
            <Link key={j.id} to={`/jobs/${j.slug || j.id}`} className="wofy-card !p-4 hover:shadow-md transition-shadow" data-testid={`biz-job-${j.slug || j.id}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{j.employment_type} · {j.category}</div>
              <div className="font-display text-lg mt-1 leading-tight">{j.title}</div>
              <div className="text-xs opacity-80 mt-1">{j.remote ? "Remote" : [j.city, j.country].filter(Boolean).join(", ")}</div>
              {j.salary && <div className="text-xs font-medium mt-1" style={{ color: "var(--wofy-primary)" }}>{j.salary}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ShopTab({ biz, isOwner, isEcommerce }) {
  if (!isEcommerce) {
    return (
      <div className="wofy-card !p-8 text-center max-w-2xl mx-auto" data-testid="shop-upsell">
        <ShoppingBag className="w-10 h-10 mx-auto mb-2" style={{ color: "var(--wofy-primary)" }} />
        <div className="font-display text-2xl mb-1">Add a shopping cart to your business</div>
        <p className="text-sm opacity-70 mt-2 mb-5 max-w-md mx-auto">
          Your business is on the <b>{biz._plan?.name || "Free"}</b> plan — a beautiful public
          website. Upgrade to an <b>E-commerce plan</b> to sell products, take orders, accept
          payments, and manage inventory — all from your Social Pulse profile.
        </p>
        {isOwner && (
          <Link to={`/business/${biz.slug}/plans`} className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="shop-see-plans">
            <Sparkles className="w-4 h-4" /> See e-commerce plans
          </Link>
        )}
      </div>
    );
  }
  // Ecommerce plans reuse the ProductsTab grid — same experience, same
  // add-to-cart flow. Owners can jump straight to "Add a product".
  return <ProductsTab biz={biz} isOwner={isOwner} />;
}

function ContactTab({ biz }) {
  return (
    <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
      <div className="space-y-3">
        {biz.contact_phone && (
          <a href={`tel:${biz.contact_phone}`} className="wofy-card !p-4 flex items-center gap-3 hover:shadow-md">
            <Phone className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
            <div><div className="text-xs opacity-70">Call</div><div className="font-medium">{biz.contact_phone}</div></div>
          </a>
        )}
        {biz.contact_email && (
          <a href={`mailto:${biz.contact_email}`} className="wofy-card !p-4 flex items-center gap-3 hover:shadow-md">
            <Mail className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
            <div><div className="text-xs opacity-70">Email</div><div className="font-medium break-all">{biz.contact_email}</div></div>
          </a>
        )}
        {biz.website && (
          <a href={biz.website} target="_blank" rel="noreferrer" className="wofy-card !p-4 flex items-center gap-3 hover:shadow-md">
            <Globe className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
            <div><div className="text-xs opacity-70">Website</div><div className="font-medium break-all">{biz.website}</div></div>
          </a>
        )}
        {biz.address && (
          <div className="wofy-card !p-4">
            <MapPin className="w-5 h-5 mb-1" style={{ color: "var(--wofy-primary)" }} />
            <div className="text-xs opacity-70 mb-0.5">Address</div>
            <div className="text-sm whitespace-pre-line">{biz.address}</div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(biz.address)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs mt-2 underline"
            >
              Get directions <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>
      <ComingSoon title="Contact form" body="Custom form that lands in the business inbox and optionally forwards to WhatsApp." />
    </div>
  );
}
